import React from 'react';
import Styles from '../BrandInsightsDashboard.module.scss';
import { FiTarget, FiGrid, FiInstagram, FiLinkedin, FiTwitter, FiYoutube, FiTrendingUp, FiHash, FiFileText, FiImage, FiVideo, FiLayers } from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa';

const PLATFORM_ICONS = {
  instagram: FiInstagram,
  linkedin: FiLinkedin,
  twitter: FiTwitter,
  youtube: FiYoutube,
  tiktok: FaTiktok,
};

const PLATFORM_COLORS = {
  instagram: '#E4405F',
  linkedin: '#0A66C2',
  twitter: '#1DA1F2',
  youtube: '#FF0000',
  tiktok: '#000000',
};

const PLATFORM_NAMES = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'Twitter/X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
};

const PILLAR_COLORS = [
  '#00a7b7', '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'
];

const ContentDNA = ({ pillars, platformDeepDives, successPatterns }) => {
  // Extract all unique topics/themes from pillars
  const allTopics = extractAllTopics(pillars);

  // Calculate format distribution from platform data
  const formatStats = calculateFormatStats(platformDeepDives);

  // Get platform-specific content strategies
  const platformStrategies = getPlatformStrategies(platformDeepDives);

  // Calculate content mix percentages
  const contentMix = calculateContentMix(platformDeepDives);

  return (
    <div className={Styles.contentDnaSection}>
      <div className={Styles.sectionHeader}>
        <FiGrid className={Styles.sectionIcon} />
        <h2>Your Content DNA</h2>
      </div>

      <div className={Styles.dnaGrid}>
        {/* Content Pillars - Visual Chart */}
        <div className={Styles.dnaCard + ' ' + Styles.pillarsCard}>
          <h3>
            <FiTarget className={Styles.cardIcon} />
            Content Pillars
          </h3>
          {pillars && pillars.length > 0 ? (
            <div className={Styles.pillarsVisualization}>
              {/* Pillar bars with colors */}
              <div className={Styles.pillarBars}>
                {pillars.slice(0, 6).map((pillar, idx) => (
                  <div key={idx} className={Styles.pillarRow}>
                    <div className={Styles.pillarInfo}>
                      <span
                        className={Styles.pillarDot}
                        style={{ backgroundColor: PILLAR_COLORS[idx % PILLAR_COLORS.length] }}
                      />
                      <span className={Styles.pillarName}>{pillar.name}</span>
                      {pillar.platforms && pillar.platforms.length > 0 && (
                        <div className={Styles.pillarPlatformIcons}>
                          {pillar.platforms.slice(0, 3).map(p => {
                            const Icon = PLATFORM_ICONS[p] || FiGrid;
                            return (
                              <Icon
                                key={p}
                                className={Styles.miniPlatformIcon}
                                style={{ color: PLATFORM_COLORS[p] }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div className={Styles.pillarBarWrapper}>
                      <div className={Styles.pillarBarTrack}>
                        <div
                          className={Styles.pillarBarFill}
                          style={{
                            width: `${Math.min(pillar.percentage, 100)}%`,
                            backgroundColor: PILLAR_COLORS[idx % PILLAR_COLORS.length]
                          }}
                        />
                      </div>
                      <span className={Styles.pillarPercentLabel}>{pillar.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary insight */}
              {pillars[0] && (
                <div className={Styles.pillarInsight}>
                  <FiTrendingUp className={Styles.insightIcon} />
                  <span>
                    <strong>{pillars[0].name}</strong> is your dominant content type at {pillars[0].percentage}%
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className={Styles.noDataSmall}>No pillar data available</p>
          )}
        </div>

        {/* Platform Content Strategy */}
        <div className={Styles.dnaCard + ' ' + Styles.platformStrategyCard}>
          <h3>
            <FiLayers className={Styles.cardIcon} />
            Platform Content Mix
          </h3>
          {platformStrategies.length > 0 ? (
            <div className={Styles.platformStrategies}>
              {platformStrategies.map((strategy, idx) => {
                const Icon = PLATFORM_ICONS[strategy.platform] || FiGrid;
                return (
                  <div key={idx} className={Styles.strategyItem}>
                    <div className={Styles.strategyHeader}>
                      <Icon
                        className={Styles.strategyPlatformIcon}
                        style={{ color: PLATFORM_COLORS[strategy.platform] }}
                      />
                      <span className={Styles.strategyPlatformName}>
                        {PLATFORM_NAMES[strategy.platform] || strategy.platform}
                      </span>
                    </div>
                    <div className={Styles.strategyPillars}>
                      {strategy.topPillars.slice(0, 3).map((pillar, pIdx) => (
                        <div key={pIdx} className={Styles.strategyPillar}>
                          <span className={Styles.strategyPillarName}>{pillar.name}</span>
                          <div className={Styles.strategyPillarBarWrapper}>
                            <div className={Styles.strategyPillarTrack}>
                              <div
                                className={Styles.strategyPillarFill}
                                style={{
                                  width: `${Math.min(pillar.percentage, 100)}%`,
                                  backgroundColor: PILLAR_COLORS[pIdx % PILLAR_COLORS.length]
                                }}
                              />
                            </div>
                            <span className={Styles.strategyPillarPercent}>{pillar.percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {strategy.strength && (
                      <p className={Styles.strategyStrength}>{strategy.strength}</p>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className={Styles.noDataSmall}>Connect platforms to see distribution</p>
          )}
        </div>

        {/* Topics & Themes */}
        {allTopics.length > 0 && (
          <div className={Styles.dnaCard + ' ' + Styles.topicsCard}>
            <h3>
              <FiHash className={Styles.cardIcon} />
              Topics & Themes You Cover
            </h3>
            <div className={Styles.topicsCloud}>
              {allTopics.map((topic, idx) => (
                <span
                  key={idx}
                  className={Styles.topicTag}
                  style={{
                    borderColor: PILLAR_COLORS[idx % PILLAR_COLORS.length],
                    color: PILLAR_COLORS[idx % PILLAR_COLORS.length]
                  }}
                >
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Content Formats */}
        <div className={Styles.dnaCard + ' ' + Styles.formatsCard}>
          <h3>
            <FiFileText className={Styles.cardIcon} />
            Content Formats
          </h3>
          {contentMix.length > 0 ? (
            <div className={Styles.formatMix}>
              {contentMix.map((format, idx) => {
                const FormatIcon = getFormatIcon(format.type);
                return (
                  <div key={idx} className={Styles.formatMixItem}>
                    <div className={Styles.formatMixIcon}>
                      <FormatIcon />
                    </div>
                    <div className={Styles.formatMixInfo}>
                      <span className={Styles.formatMixName}>{format.name}</span>
                      <span className={Styles.formatMixPercent}>{format.percentage}%</span>
                    </div>
                    <div className={Styles.formatMixBarWrapper}>
                      <div
                        className={Styles.formatMixBar}
                        style={{ width: `${format.percentage}%` }}
                      />
                    </div>
                    {format.performance && (
                      <span className={Styles.formatMixPerf}>
                        {format.performance > 0 ? '+' : ''}{format.performance.toFixed(1)}% eng
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : formatStats.length > 0 ? (
            <div className={Styles.formatList}>
              {formatStats.map((format, idx) => (
                <div key={idx} className={Styles.formatItem}>
                  <span className={Styles.formatName}>{format.name}</span>
                  <span className={Styles.formatDesc}>{format.description}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className={Styles.noDataSmall}>Format analysis in progress...</p>
          )}
        </div>

        {/* Success Patterns - Full Width */}
        {successPatterns && hasPatterns(successPatterns) && (
          <div className={Styles.dnaCard + ' ' + Styles.fullWidth + ' ' + Styles.patternsCard}>
            <h3>
              <FiTrendingUp className={Styles.cardIcon} />
              Patterns Across Your Best Content
            </h3>
            <div className={Styles.patternsGrid}>
              {successPatterns.engagement_drivers?.length > 0 && (
                <div className={Styles.patternCategory}>
                  <h4>Engagement Drivers</h4>
                  <ul>
                    {successPatterns.engagement_drivers.slice(0, 3).map((pattern, idx) => (
                      <li key={idx}>{pattern}</li>
                    ))}
                  </ul>
                </div>
              )}
              {successPatterns.format_patterns?.length > 0 && (
                <div className={Styles.patternCategory}>
                  <h4>Format Insights</h4>
                  <ul>
                    {successPatterns.format_patterns.slice(0, 3).map((pattern, idx) => (
                      <li key={idx}>{pattern}</li>
                    ))}
                  </ul>
                </div>
              )}
              {successPatterns.hook_patterns?.length > 0 && (
                <div className={Styles.patternCategory}>
                  <h4>Hook Effectiveness</h4>
                  <ul>
                    {successPatterns.hook_patterns.slice(0, 3).map((pattern, idx) => (
                      <li key={idx}>{pattern}</li>
                    ))}
                  </ul>
                </div>
              )}
              {successPatterns.length_patterns?.length > 0 && (
                <div className={Styles.patternCategory}>
                  <h4>Length Insights</h4>
                  <ul>
                    {successPatterns.length_patterns.slice(0, 3).map((pattern, idx) => (
                      <li key={idx}>{pattern}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Extract all unique topics from pillars
const extractAllTopics = (pillars) => {
  if (!pillars) return [];

  const topics = new Set();

  pillars.forEach(pillar => {
    // Add pillar name
    if (pillar.name) topics.add(pillar.name);

    // Add example themes if available
    if (pillar.example_themes) {
      pillar.example_themes.forEach(theme => topics.add(theme));
    }

    // Add description keywords
    if (pillar.description) {
      // Extract key phrases from description
      const keywords = pillar.description
        .split(/[,.]/)
        .map(s => s.trim())
        .filter(s => s.length > 3 && s.length < 30);
      keywords.slice(0, 2).forEach(k => topics.add(k));
    }
  });

  return Array.from(topics).slice(0, 12);
};

// Get platform-specific strategies
const getPlatformStrategies = (platformDeepDives) => {
  if (!platformDeepDives) return [];

  return Object.entries(platformDeepDives).map(([platform, data]) => {
    const pillars = data.content_pillars || [];
    const voice = data.voice_fingerprint || {};

    // Generate a strength description based on the data
    let strength = '';
    if (pillars[0]) {
      const topPillar = pillars[0].name?.toLowerCase();
      if (topPillar?.includes('education')) {
        strength = 'Strong for teaching and how-tos';
      } else if (topPillar?.includes('engagement')) {
        strength = 'Great for community building';
      } else if (topPillar?.includes('promotion')) {
        strength = 'Effective for conversions';
      } else if (topPillar?.includes('story')) {
        strength = 'Authentic storytelling works here';
      } else if (topPillar?.includes('inspiration')) {
        strength = 'Motivational content resonates';
      }
    }

    return {
      platform,
      topPillars: pillars.slice(0, 3).map(p => ({
        name: p.name,
        percentage: p.percentage || Math.round((p.post_count / (data.posts_analyzed || 1)) * 100)
      })),
      strength,
      voice: voice.tone
    };
  });
};

// Calculate content mix from platform data
const calculateContentMix = (platformDeepDives) => {
  if (!platformDeepDives) return [];

  const formatCounts = {};
  const formatEngagement = {};
  let totalPosts = 0;

  Object.values(platformDeepDives).forEach(data => {
    // Try to get from frequency_stats content_mix
    const contentMix = data.frequency_stats?.content_mix?.types || {};
    const formatPerf = data.patterns?.format_performance || {};

    Object.entries(contentMix).forEach(([format, count]) => {
      formatCounts[format] = (formatCounts[format] || 0) + count;
      totalPosts += count;
    });

    Object.entries(formatPerf).forEach(([format, stats]) => {
      if (stats.avg_engagement) {
        formatEngagement[format] = stats.avg_engagement;
      }
    });

    // Also check top_performers for format info
    (data.top_performers || []).forEach(post => {
      if (post.content_format && post.content_format !== 'unknown') {
        formatCounts[post.content_format] = (formatCounts[post.content_format] || 0) + 1;
        totalPosts += 1;
      }
    });
  });

  if (totalPosts === 0) return [];

  return Object.entries(formatCounts)
    .filter(([format]) => format !== 'unknown')
    .map(([format, count]) => ({
      type: format,
      name: formatFormatName(format),
      percentage: Math.round((count / totalPosts) * 100),
      performance: formatEngagement[format] || null
    }))
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 5);
};

// Calculate format statistics from platform data
const calculateFormatStats = (platformDeepDives) => {
  if (!platformDeepDives) return [];

  const formats = [];

  Object.entries(platformDeepDives).forEach(([platform, data]) => {
    const formatPerf = data.patterns?.format_performance || {};

    Object.entries(formatPerf).forEach(([format, stats]) => {
      if (stats.avg_engagement > 2) {
        formats.push({
          name: formatFormatName(format),
          description: `${stats.avg_engagement.toFixed(1)}% avg engagement on ${PLATFORM_NAMES[platform] || platform}`,
          engagement: stats.avg_engagement,
          platform
        });
      }
    });
  });

  // Sort by engagement and dedupe
  const uniqueFormats = [];
  const seen = new Set();

  formats
    .sort((a, b) => b.engagement - a.engagement)
    .forEach(f => {
      if (!seen.has(f.name)) {
        seen.add(f.name);
        uniqueFormats.push(f);
      }
    });

  return uniqueFormats.slice(0, 4);
};

const formatFormatName = (format) => {
  const formatMap = {
    'carousel': 'Carousels',
    'image': 'Static Images',
    'video': 'Video',
    'reel': 'Reels/Shorts',
    'text': 'Text Posts',
    'thread': 'Threads',
    'story': 'Stories',
    'live': 'Live Video'
  };
  return formatMap[format] || format.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getFormatIcon = (format) => {
  const iconMap = {
    'carousel': FiLayers,
    'image': FiImage,
    'video': FiVideo,
    'reel': FiVideo,
    'text': FiFileText,
    'thread': FiFileText,
  };
  return iconMap[format] || FiFileText;
};

const hasPatterns = (patterns) => {
  if (!patterns) return false;
  return (patterns.engagement_drivers?.length > 0) ||
         (patterns.format_patterns?.length > 0) ||
         (patterns.hook_patterns?.length > 0) ||
         (patterns.length_patterns?.length > 0);
};

export default ContentDNA;
